import { useMemo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { useAuthWrapper } from '@weareopenr/authkit';
import { FEATURE_FLAGS } from 'src/constants';
import { getCrossSectionDuplicateItemIds } from 'src/helpers/getCrossSectionDuplicateItemIds';
import { ISection } from 'src/types/menu.types';

export interface IUseMenuSectionDragAndDropOptions {
  section: ISection;
  sections: ISection[];
  isUnassigned: boolean;
  isViewOnly?: boolean;
  previewedItemId?: string | null;
  isDragSuspended?: boolean;
}

export interface IMenuSectionDragAndDrop {
  canReceiveDrops: boolean;
  isDragEnabled: boolean;
  hasDuplicateItemIds: boolean;
  previewedCardId: string | null;
  isDropTarget: boolean;
  sortableItemIds: string[];
  isItemDraggable: (itemId: string) => boolean;
  setDropZoneRef: (element: HTMLElement | null) => void;
}

export const useMenuSectionDragAndDrop = ({
  section,
  sections,
  isUnassigned,
  isViewOnly,
  previewedItemId,
  isDragSuspended,
}: IUseMenuSectionDragAndDropOptions): IMenuSectionDragAndDrop => {
  const { sectionId, items } = section;
  const { featureFlags } = useAuthWrapper();

  const itemIds = useMemo(() => items.map(({ itemId }) => itemId), [items]);
  // The reorder endpoint addresses an item by id, so two cards sharing one id are
  // indistinguishable to the server - and to dnd-kit, which needs unique ids. Such a section
  // renders exactly as before but cannot be dragged out of.
  const hasDuplicateItemIds = useMemo(() => new Set(itemIds).size !== itemIds.length, [itemIds]);
  const crossSectionDuplicateIds = useMemo(() => getCrossSectionDuplicateItemIds(sections), [sections]);

  const canReceiveDrops =
    featureFlags.includes(FEATURE_FLAGS.MENU_ITEM_DRAG_AND_DROP) && !isUnassigned && !isViewOnly && !!sectionId;
  const isDragEnabled = canReceiveDrops && !hasDuplicateItemIds && !isDragSuspended;
  const previewedCardId =
    canReceiveDrops && previewedItemId && itemIds.includes(previewedItemId) ? previewedItemId : null;

  const sortableItemIds = useMemo(
    () => itemIds.filter((itemId) => itemId !== previewedCardId && !crossSectionDuplicateIds.has(itemId)),
    [itemIds, previewedCardId, crossSectionDuplicateIds],
  );

  const { setNodeRef: setDropZoneRef } = useDroppable({
    id: sectionId,
    data: { sectionId },
    disabled: !canReceiveDrops,
  });

  return {
    canReceiveDrops,
    isDragEnabled,
    hasDuplicateItemIds,
    previewedCardId,
    isDropTarget: previewedCardId !== null,
    sortableItemIds,
    isItemDraggable: (itemId) => isDragEnabled && !crossSectionDuplicateIds.has(itemId),
    setDropZoneRef,
  };
};
